import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bot, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gradeLabels = {
  7: 'Kansas History', 8: 'U.S. History', 9: 'World History',
  10: 'Modern World', 11: 'U.S. History', 12: 'Government & Economics'
};

export default function EssayGraderIndex() {
  const navigate = useNavigate();
  const [unitsByGrade, setUnitsByGrade] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeGrade, setActiveGrade] = useState(7);

  useEffect(() => {
    const load = async () => {
      const grades = [7, 8, 9, 10, 11, 12];
      const results = {};
      for (const g of grades) {
        try {
          const res = await axios.get(`${API}/units/grade/${g}`);
          results[g] = res.data;
        } catch { results[g] = []; }
      }
      setUnitsByGrade(results);
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-[#FEFAF0] flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-amber-800" />
    </div>
  );

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <div className="mb-8 flex items-center gap-3">
          <Bot className="h-6 w-6 text-[#D4AF37]" />
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">AI Tools</p>
            <h1 className="font-serif-display text-3xl text-[#14213D] tracking-tight">AI Essay Grader</h1>
            <p className="text-sm text-[#5A6577] mt-1 max-w-2xl">
              Upload student essays to be graded against unit rubrics — AI-powered feedback, plagiarism detection, and scoring.
            </p>
          </div>
        </div>

        {/* Grade Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[7, 8, 9, 10, 11, 12].map(g => (
            <button key={g}
              onClick={() => setActiveGrade(g)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeGrade === g ? 'bg-[#14213D] text-[#FDFBF7]' : 'bg-white text-[#2D3748] hover:bg-[#F9F8F4] border border-[#EBE7E0]'}`}
              data-testid={`grade-tab-${g}`}
            >
              Grade {g}: {gradeLabels[g]}
            </button>
          ))}
        </div>

        {/* Units Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(unitsByGrade[activeGrade] || []).map((unit, i) => (
            <Card key={unit.id}
              className="group cursor-pointer border border-[#EBE7E0] shadow-sm hover:shadow-md hover:-translate-y-1 transition-all bg-white relative overflow-hidden"
              onClick={() => navigate(`/essay-grader/${unit.id}`)}
              data-testid={`essay-card-${i}`}
            >
              <div className="absolute top-0 left-0 w-full h-0.5 bg-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-mono tracking-widest uppercase text-[#7A93AC]">Unit {unit.unit_number}</span>
                  <Bot className="h-4 w-4 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif-display text-[#14213D] text-base mb-2 leading-snug">{unit.title}</h3>
                <p className="text-xs text-[#5A6577] mb-3">
                  {unit.essay_prompts?.length || 0} essay prompts available
                </p>
                <div className="flex items-center text-xs text-[#14213D] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>Grade Essays</span>
                  <ChevronRight className="h-3 w-3 ml-1" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
